import {
  useState,
  useEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQueryClient
} from '@tanstack/react-query';

import {
  Input,
  Modal,
  StatusIndicator
} from '@unified-checkout/design-system';

import {
  MODAL_STEP_STATUS,
  UPDATE_BILLS_STATUS
} from '@app/dashboard/constants/Bills';
import { updateSavedBills } from '@app/dashboard/services/inner_layout/bills/update-saved-bills';
import type { IUpdateSavedBills } from '@app/dashboard/types/innerLayout/bills/update-saved-bills';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { THREE_SECONDS } from '@app/dashboard/constants/shared';

import { isStatusVisible } from '../../../../utils';

import styles from './update-saved-bills.module.scss';

export const UpdateSavedBillsModal = ({
  showUpdateModal,
  setShowUpdateModal,
  selectedBill
}: IUpdateSavedBills) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>(MODAL_STEP_STATUS.CONFIRM);
  const [formData, setFormData] = useState({
    service_name: '',
    name: '',
    sub_title: '',
  });

  const {
    mutate: handleUpdateSavedBill,
    isPending: isUpdateLoading,
    data: updateData,
  } = useMutation({
    mutationFn: (payload: any) => updateSavedBills(payload),
    onSuccess: () => {
      setStatus(MODAL_STEP_STATUS.SUCCESS);
      setTimeout(() => {
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.GET_SAVED_BILLS] });
        setShowUpdateModal(false);
        setStatus(MODAL_STEP_STATUS.CONFIRM);
      }, THREE_SECONDS);
    },
    onError: () => {
      setStatus(MODAL_STEP_STATUS.FAILED);
      setTimeout(() => {
        setStatus(MODAL_STEP_STATUS.CONFIRM);
      }, THREE_SECONDS);
    }
  });

  useEffect(() => {
    if (showUpdateModal && selectedBill) {
      setFormData({
        service_name: selectedBill.service_name || '',
        name: selectedBill.name || '',
        sub_title: selectedBill.sub_title || '',
      });
    }
  }, [showUpdateModal, selectedBill]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    handleUpdateSavedBill({
      id: selectedBill?.id,
      ...formData,
    });
  }

  const inputs = [
    {
      label: t('innerLayout.billsPage.savedBills.updateModal.billProvider'),
      name: 'service_name',
      value: selectedBill?.service_name || '',
      // onChange: handleInputChange,
      disabled: true,
    },
    {
      label: t('innerLayout.billsPage.savedBills.updateModal.billName'),
      name: 'name',
      value: formData.name,
      placeholder: t('innerLayout.billsPage.savedBills.updateModal.billNamePlaceholder'),
      onChange: handleInputChange,
      status: !formData.name ? 'error' : undefined,
      error: !formData.name ? t('innerLayout.billsPage.savedBills.updateModal.billNameError') : '',
    },
    {
      label: t('innerLayout.billsPage.savedBills.updateModal.billReferenceName'),
      name: 'sub_title',
      value: formData.sub_title,
      onChange: handleInputChange,
      placeholder: t('innerLayout.billsPage.savedBills.updateModal.billReferenceNamePlaceholder'),
      status: !formData.sub_title ? ('error' as any) : undefined,
      error: !formData.sub_title ? t('innerLayout.billsPage.savedBills.updateModal.billNameError') : '',
    }
  ];

  const isChanged =
    formData.name !== (selectedBill?.name || '') ||
    formData.sub_title !== (selectedBill?.sub_title || '');

  const getBtnsProps = () => {
    if (isStatusVisible(status)) return;
    return [
      {
        text: t('form.save'),
        onClickHandler: handleSave,
        loading: isUpdateLoading,
        disabled: isUpdateLoading || !isChanged || !formData.name || !formData.sub_title,
      },
      {
        text: t('form.cancel'),
        onClickHandler: () => setShowUpdateModal(false),
        color: 'secondary' as const,
        variant: 'outlined' as const,
      }
    ]
  }

  const updateStatusContent = UPDATE_BILLS_STATUS(t).find((item) => item.id === status);

  return (
    <Modal
      open={showUpdateModal}
      title={!isStatusVisible(status) ? t('innerLayout.billsPage.savedBills.updateModal.title') : ''}
      hasCloseIcon
      onCloseHandler={() => setShowUpdateModal(false)}
      btnsProps={getBtnsProps()}
    >
      <div className={styles['update-saved-bills']}>
        {isStatusVisible(status) ? (
          <StatusIndicator
            status={updateStatusContent?.status}
            icon={updateStatusContent?.icon}
            //text={updateData?.data?.status}
            hintText={updateData?.data?.message || updateStatusContent?.hintText}
          />
        ) : (
          inputs.map((input, index) => (
            <Input key={index} {...input} />
          ))
        )}
      </div>
    </Modal>
  );
}
